<template>
  <el-tabs
    :value="activeTab"
    class="member-page-tab"
    type="card"
    @tab-click="handleTabClick"
  >
    <el-tab-pane
      v-for="tab in displayTabOptions"
      :key="tab.name"
      :label="tab.label"
      :name="tab.name"
    >
      <!-- 訊息提醒 tab 使用 slot -->
      <slot
        v-if="tab.name === 'notify' && activeTab === 'notify'"
        name="notify-tab"
      />

      <!-- 其他 tab 使用動態組件 -->
      <component
        :is="tab.component"
        v-else-if="activeTab === tab.name && tab.component"
        v-bind="tab.props"
        v-on="tab.listeners"
      />
    </el-tab-pane>
  </el-tabs>
</template>

<script>
import { defineComponent, computed } from 'vue'
import { t } from '@/plugins/i18n/i18n'
import { usePermissions } from '@/use/permissions'

// Import all tab components
import BeautyAI360Analysis from '../../Tab/BeautyAI360Analysis/BeautyAI360Analysis.vue'
import ReservationTab from '../../Tab/ReservationTab.vue'
import EntryRecordTab from '../../Tab/EntryRecordTab/EntryRecordTab.vue'
import WalletTab from '../../Tab/WalletTab.vue'
import RebateTab from '../../Tab/RebateTab/RebateTab.vue'
import PointTab from '../../Tab/PointTab/PointTab.vue'
import PointCardTab from '../../Tab/PointCardTab/PointCardTab.vue'
import ClassTicketTab from '../../Tab/ClassTicketTab/ClassTicketTab.vue'
import PunchCardTab from '../../Tab/PunchCardTab/PunchCardTab.vue'
import SalesRecordTab from '../../Tab/SalesRecordTab/SalesRecordTab.vue'
import MemberHistoryTab from '../../Tab/MemberHistoryTab.vue'
import MemberInfoTab from '../../Tab/MemberInfoTab/MemberInfoTab.vue'
import MemberCustomProfileTab from '../../Tab/MemberCustomProfileTab.vue'
import MemberAddressTab from '../../Tab/MemberAddressTab.vue'
import LotteryHistoryTab from '../../Tab/LotteryHistoryTab.vue'
import CouponHistoryTab from '../../Tab/CouponHistoryTab.vue'
import TicketBoxHistoryTab from '../../Tab/TicketBoxHistoryTab.vue'
import OrderRecord from '../../Tab/OrderRecord.vue'
import MemberShopOrderRecordTab from '../../Tab/MemberShopOrderRecordTab.vue'
import MemberSubscriptionRecordTab from '../../Tab/MemberSubscriptionRecordTab.vue'
import PeriodicBenefitTab from '../../Tab/PeriodicBenefitTab/PeriodicBenefitTab.vue'
import ExternalTransactionTab from '../../Tab/ExternalTransactionTab/ExternalTransactionTab.vue'
import OrgExternalTransactionTab from '../../Tab/ExternalTransactionTab/OrgExternalTransactionTab.vue'
import HeadquarterExternalTransactionTab from '../../Tab/ExternalTransactionTab/HeadquarterExternalTransactionTab.vue'
import ExternalWalletTab from '../../Tab/ExternalWalletTab.vue'
import VehicleRecordTab from '../../Tab/VehicleRecordTab/index.vue'
import AccessControlRecordTab from '../../Tab/AccessControlRecordTab/AccessControlRecordTab.vue'
import SignatureDistributionRecords from '@/views/SignatureDistributionRecords/SignatureDistributionRecords.vue'
import MessageBlock from '@/components/Message/MessageBlock.vue'
import LinePushMessageLog from '@/views/Basic/LinePushMessageLog.vue'

export default defineComponent({
  name: 'MemberProfileTabs',

  components: {
    BeautyAI360Analysis,
    ReservationTab,
    EntryRecordTab,
    WalletTab,
    RebateTab,
    PointTab,
    PointCardTab,
    ClassTicketTab,
    PunchCardTab,
    SalesRecordTab,
    MemberHistoryTab,
    MemberInfoTab,
    MemberCustomProfileTab,
    MemberAddressTab,
    LotteryHistoryTab,
    CouponHistoryTab,
    TicketBoxHistoryTab,
    OrderRecord,
    MemberShopOrderRecordTab,
    MemberSubscriptionRecordTab,
    PeriodicBenefitTab,
    ExternalTransactionTab,
    OrgExternalTransactionTab,
    HeadquarterExternalTransactionTab,
    ExternalWalletTab,
    VehicleRecordTab,
    AccessControlRecordTab,
    SignatureDistributionRecords,
    MessageBlock,
    LinePushMessageLog,
  },

  props: {
    activeTab: {
      type: String,
      required: true,
    },
    member: {
      type: Object,
      required: true,
    },
    memberId: {
      type: String,
      required: true,
    },
    shopPoints: {
      type: Array,
      default: () => [],
    },
    levelList: {
      type: Array,
      default: () => [],
    },
    rebateWalletBalance: {
      type: Number,
      default: 0,
    },
    pointWalletBalance: {
      type: Object,
      default: () => ({}),
    },
    useShopRebate: {
      type: Boolean,
      default: false,
    },
    externalWalletName: {
      type: String,
      default: '',
    },
    notifyMessageStack: {
      type: Array,
      default: () => [],
    },
    onError: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'tab-click',
    'update:activeTab',
    'create-reservation',
    'update-wallet',
    'update-point',
    'message-error',
    'message-checked',
    'remove-notify-message',
    'add-message-block',
    'reset-message',
    'send-message',
    'update-member',
  ],

  setup (props, { emit }) {
    const { checkAction, checkActionList } = usePermissions()

    // Feature Keys
    const featureKeys = {
      useBeautyAIMemberAnalysis: 'admin.beautyAIMemberAnalysis.page',
      useAppointmentFeature: 'admin.appointmentReservation.page',
      useEntryRecordFeature: 'admin.entryControlRecord.page',
      useWalletPermission: 'admin.member.adminUseWallet',
      useDepositWalletPermission: 'admin.member.depositWallet',
      useWalletRecordPermission: 'admin.member.findWalletRecord',
      usePointCardFeature: 'admin.pointCard.page',
      useClassTicketFeature: 'admin.classTicketRecord.page',
      usePunchCardFeature: 'admin.punchCard.page',
      useSalesRecordFeature: 'admin.salesRecord.page',
      useMessageNotification: 'admin.member.sentLineMessage',
      useLinePushLog: 'admin.linePushMessageLog.page',
      useMemberHistory: 'admin.memberHistory.page',
      useMemberCustomProfile: 'planModule.foodpandaRestaurant.*',
      useMemberAddress: 'adminView.member.findUserInfoMoreAddress',
      useMemberGameFeature: 'admin.memberGameRecord.memberPage',
      useCouponFeature: 'admin.couponRecord.memberPage',
      useMemberOrder: 'admin.ecommerceOrder.page',
      useMemberShopOrder: 'adminView.memberStoreOrder.page',
      useSubscription: 'admin.subscriptionOrder.page',
      usePeriodicBenefit: 'admin.periodicBenefitRecord.page',
      useSignatureRecord: 'admin.signatureRecordMapping.find',
      // CNCY-10121：會員檔案內「第三方交易紀錄」tab 改用 .find（讀單一會員紀錄，即本 tab 實際呼叫的
      // GetMemberExternalTransaction），與「第三方消費紀錄」獨立模組選單（gate=.page）解耦。
      // 這讓角色可只開單一會員 tab（.find:true）而隱藏分店業績模組（.page:false）。
      useExternalTransaction: 'admin.memberExternalTransaction.find',
      useOrgExternalTransaction: 'admin.orgExternalTransaction.page',
      useHeadquarterExternalTransaction: 'admin.headquarterExternalTransaction.page',
      useExternalWallet: 'admin.externalWallet.page',
      useKeeper: 'admin.keeperVehicle.findAllByMember',
      useAccessControlRecord: 'admin.miezoAccessControlPassword.find',
    }

    // Computed permissions
    const useShopPoint = computed(() => {
      return checkActionList(
        [
          'admin.member.findOnePointWallet',
          'admin.member.addPointWalletRecord',
        ],
        'or',
      )
    })

    const showPointTabs = computed(() => {
      return props.shopPoints.filter((point) => {
        const pointFeature = point.feature || {}
        const shouldUseWallet = pointFeature.usePointWalletRecord
        const isExternal = point.type === 'external'
        const shouldHideExternal = isExternal && checkAction('planModule.emisHidePoints.*')
        return (
          pointFeature.findPointWalletRecord ||
          pointFeature.addPointWalletRecord ||
          (shouldUseWallet && !shouldHideExternal)
        )
      })
    })

    // Display tab options - 統一管理所有 tab 的配置
    const displayTabOptions = computed(() => {
      const tabs = []

      // 特質雷達圖
      if (checkAction(featureKeys.useBeautyAIMemberAnalysis)) {
        tabs.push({
          name: '360Analysis',
          label: t('member.memberProfileTabs.radarChart'),
          component: BeautyAI360Analysis,
          props: {
            member: props.member,
          },
        })
      }

      // 預約
      if (checkAction(featureKeys.useAppointmentFeature)) {
        tabs.push({
          name: 'reservation',
          label: t('member.memberProfileTabs.reservationStatus'),
          component: ReservationTab,
          props: {
            member: props.member,
            externalWalletName: props.externalWalletName,
          },
          listeners: {
            create: () => emit('create-reservation'),
          },
        })
      }

      // 進場紀錄
      if (checkAction(featureKeys.useEntryRecordFeature)) {
        tabs.push({
          name: 'entryRecord',
          label: t('member.memberProfileTabs.entryRecord'),
          component: EntryRecordTab,
          props: {
            member: props.member,
          },
        })
      }

      // 儲值金
      const showWallet = checkAction(featureKeys.useWalletPermission) ||
                         checkAction(featureKeys.useDepositWalletPermission) ||
                         checkAction(featureKeys.useWalletRecordPermission)

      if (showWallet) {
        tabs.push({
          name: 'wallet',
          label: t('member.memberProfileTabs.depositBalance'),
          component: WalletTab,
          props: {
            member: props.member,
          },
          listeners: {
            updateWallet: () => emit('update-wallet'),
          },
        })
      }

      // 回饋金
      if (props.useShopRebate) {
        tabs.push({
          name: 'rebate',
          label: t('member.memberProfileTabs.rebateBalance'),
          component: RebateTab,
          props: {
            memberId: props.memberId,
            rebateWalletBalance: props.rebateWalletBalance,
          },
          listeners: {
            updateWallet: () => emit('update-wallet'),
          },
        })
      }

      // 點數 (動態)
      if (useShopPoint.value) {
        showPointTabs.value.forEach((point) => {
          tabs.push({
            name: point.key,
            label: point.name,
            component: PointTab,
            props: {
              memberId: props.memberId,
              member: props.member,
              pointWalletBalance: props.pointWalletBalance,
              shopPointKey: point.key,
              pointFeatrue: point.feature,
            },
            listeners: {
              updatePoint: () => emit('update-point'),
            },
          })
        })
      }

      // 點數卡
      if (checkAction(featureKeys.usePointCardFeature)) {
        tabs.push({
          name: 'pointCard',
          label: t('member.memberProfileTabs.pointCardRecord'),
          component: PointCardTab,
          props: {
            member: props.member,
          },
        })
      }

      // 堂票
      if (checkAction(featureKeys.useClassTicketFeature)) {
        tabs.push({
          name: 'classTicket',
          label: t('member.memberProfileTabs.classTicketRecord'),
          component: ClassTicketTab,
          props: {
            member: props.member,
          },
        })
      }

      // 次卡
      if (checkAction(featureKeys.usePunchCardFeature)) {
        tabs.push({
          name: 'punchCard',
          label: t('member.memberProfileTabs.punchCardRecord'),
          component: PunchCardTab,
          props: {
            member: props.member,
          },
        })
      }

      // 銷售紀錄
      if (checkAction(featureKeys.useSalesRecordFeature)) {
        tabs.push({
          name: 'salesRecord',
          label: t('member.memberProfileTabs.salesRecord'),
          component: SalesRecordTab,
          props: {
            member: props.member,
            useShopRebate: props.useShopRebate,
          },
        })
      }

      // 訊息提醒
      if (checkAction(featureKeys.useMessageNotification)) {
        tabs.push({
          name: 'notify',
          label: t('member.memberProfileTabs.messageNotification'),
          component: null, // 這個 tab 需要特殊處理，保留在父組件
        })
      }

      // LINE 通知紀錄
      if (checkAction(featureKeys.useLinePushLog)) {
        tabs.push({
          name: 'linePushLog',
          label: t('member.memberProfileTabs.lineNotificationRecord'),
          component: LinePushMessageLog,
          props: {
            memberId: props.memberId,
            hideTitle: true,
            hideFilters: ['member'],
            hideTableColumns: ['Member.UserInfo.name', 'Member.UserInfo.phone'],
            hideStatistics: true,
          },
        })
      }

      // 服務歷程
      if (checkAction(featureKeys.useMemberHistory)) {
        tabs.push({
          name: 'memberHistory',
          label: t('member.memberProfileTabs.serviceHistory'),
          component: MemberHistoryTab,
          props: {
            member: props.member,
          },
        })
      }

      // 會員資訊 (always show)
      tabs.push({
        name: 'info',
        label: t('member.memberProfileTabs.memberInfo'),
        component: MemberInfoTab,
        props: {
          member: props.member,
          levelList: props.levelList,
        },
        listeners: {
          update: () => emit('update-member'),
        },
      })

      // 餐廳資訊
      if (checkAction(featureKeys.useMemberCustomProfile)) {
        tabs.push({
          name: 'customProfile',
          label: t('member.memberProfileTabs.restaurantInfo'),
          component: MemberCustomProfileTab,
          props: {
            type: 'foodpandaRestaurant',
            memberId: props.memberId,
          },
        })
      }

      // 常用收件地點
      if (checkAction(featureKeys.useMemberAddress)) {
        tabs.push({
          name: 'memberAddress',
          label: t('member.memberProfileTabs.frequentDeliveryLocation'),
          component: MemberAddressTab,
          props: {
            memberId: props.memberId,
          },
        })
      }

      // 抽獎紀錄
      if (checkAction(featureKeys.useMemberGameFeature)) {
        tabs.push({
          name: 'lotteryHistory',
          label: t('member.tab.lotteryHistory.name'),
          component: LotteryHistoryTab,
          props: {
            memberId: props.memberId,
          },
        })
      }

      // 使用券紀錄
      if (checkAction(featureKeys.useCouponFeature)) {
        tabs.push({
          name: 'couponHistory',
          label: t('member.memberProfileTabs.ticketRecord'),
          component: CouponHistoryTab,
          props: {
            memberId: props.memberId,
          },
        })
      }

      // 票匣紀錄
      if (checkAction('admin.ticketBoxRecord.page')) {
        tabs.push({
          name: 'ticketBoxHistory',
          label: t('member.memberProfileTabs.ticketBoxRecord'),
          component: TicketBoxHistoryTab,
          props: {
            memberId: props.memberId,
          },
        })
      }

      // 訂單紀錄
      if (checkAction(featureKeys.useMemberOrder)) {
        tabs.push({
          name: 'orderRecord',
          label: t('member.tab.orderRecord.name'),
          component: OrderRecord,
          props: {
            memberId: props.memberId,
          },
        })
      }

      // 同意書
      if (checkAction(featureKeys.useSignatureRecord)) {
        tabs.push({
          name: 'signatureRecord',
          label: t('member.memberProfileTabs.agreement'),
          component: SignatureDistributionRecords,
          props: {
            memberId: props.memberId,
            hideTitle: true,
            hideExport: true,
            hideFilters: ['member'],
            hideTableColumns: ['Member.UserInfo.name', 'Member.UserInfo.phone'],
          },
        })
      }

      // 商城訂單
      if (checkAction(featureKeys.useMemberShopOrder)) {
        tabs.push({
          name: 'memberShopOrderRecord',
          label: t('member.memberProfileTabs.shopOrder'),
          component: MemberShopOrderRecordTab,
          props: {
            memberId: props.memberId,
          },
        })
      }

      // 訂閱紀錄
      if (checkAction(featureKeys.useSubscription)) {
        tabs.push({
          name: 'subscriptionRecord',
          label: t('member.tab.subscriptionRecord.name'),
          component: MemberSubscriptionRecordTab,
          props: {
            member: props.member,
          },
        })
      }

      // 會員權益
      if (checkAction(featureKeys.usePeriodicBenefit)) {
        tabs.push({
          name: 'periodicBenefit',
          label: t('member.memberProfileTabs.memberBenefit'),
          component: PeriodicBenefitTab,
          props: {
            member: props.member,
          },
        })
      }

      // 總部第三方消費紀錄優先於舊組織版，避免同一會員頁同時顯示兩個相同用途的 Tab。
      const useHeadquarterExternalTransaction = checkAction(featureKeys.useHeadquarterExternalTransaction)
      const useOrgExternalTransaction = checkAction(featureKeys.useOrgExternalTransaction)
      if (useHeadquarterExternalTransaction) {
        tabs.push({
          name: 'headquarterExternalTransaction',
          label: '總部第三方消費紀錄',
          component: HeadquarterExternalTransactionTab,
          props: {
            member: props.member,
          },
        })
      } else if (useOrgExternalTransaction) {
        tabs.push({
          name: 'orgExternalTransaction',
          label: '組織第三方消費紀錄',
          component: OrgExternalTransactionTab,
          props: {
            member: props.member,
          },
        })
      } else if (checkAction(featureKeys.useExternalTransaction)) {
        tabs.push({
          name: 'externalTransaction',
          label: t('member.memberProfileTabs.thirdPartyTransaction'),
          component: ExternalTransactionTab,
          props: {
            member: props.member,
          },
        })
      }

      // 會員餘額
      if (checkAction(featureKeys.useExternalWallet)) {
        tabs.push({
          name: 'externalWallet',
          label: props.externalWalletName,
          component: ExternalWalletTab,
          props: {
            member: props.member,
            externalWalletName: props.externalWalletName,
          },
          listeners: {
            updateWallet: () => emit('update-wallet'),
          },
        })
      }

      // 車輛紀錄
      if (checkAction(featureKeys.useKeeper)) {
        tabs.push({
          name: 'vehicleRecord',
          label: t('member.tab.vehicleRecord.name'),
          component: VehicleRecordTab,
        })
      }

      // 門禁紀錄
      if (checkAction(featureKeys.useAccessControlRecord)) {
        tabs.push({
          name: 'accessControlRecord',
          label: t('member.tab.accessControlRecord.name'),
          component: AccessControlRecordTab,
          props: {
            member: props.member,
          },
        })
      }

      return tabs
    })

    const handleTabClick = ({ name }) => {
      emit('tab-click', { name })
      emit('update:activeTab', name)
    }

    return {
      displayTabOptions,
      handleTabClick,
    }
  },
})
</script>

<style scoped lang="scss">
// 繼承原本的 styles
</style>
